'use client'

import { motion } from 'framer-motion'
import { useTranslations } from 'next-intl'

import { Skeleton } from '@/components/ui/skeleton'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { ExchangeIcon } from '@/components/widget/exchange-icon'
import {
  HOME_LIST,
  HOME_ROW,
  HOME_SOFT_SPRING,
  HomeMeter,
  HomePanel,
} from '@/features/home/components/home-motion'
import { useHomePerformance } from '@/features/home/hooks/use-home-performance'
import { useHomeTradeStats } from '@/features/home/hooks/use-home-trade-stats'
import type { DayRecord } from '@/features/home/lib/home-performance'
import { useNumberFormat } from '@/hooks/use-number-format'
import { getExchangeIconKey, getExchangeLabel } from '@/lib/configs/exchange-registry'
import { cn } from '@/lib/utils'

const DAY_MS = 86_400_000
const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const

function shortDate(ms: number): string {
  return new Date(ms).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
}

/** Monday-first weekday index of a UTC day. */
function weekdayIndex(dayMs: number): number {
  return (new Date(dayMs).getUTCDay() + 6) % 7
}

/**
 * The last 30 days of account value as a calendar (OKX/Webull's PnL calendar): each day green or
 * red by its change, darker for bigger moves, with up/down days, the best and worst day and the
 * worst drawdown beside it. Built from the equity curve, so transfers show as moves — said under it.
 */
export function HomePerformance({ className }: { className?: string }) {
  const t = useTranslations('trade.home.performance')
  const fmt = useNumberFormat()
  const { summary, isPending } = useHomePerformance()
  const peak = Math.max(
    0.01,
    ...(summary?.days.map((day) => Math.abs(day.changePercent ?? 0)) ?? [0])
  )
  const leading = summary?.days[0] ? weekdayIndex(summary.days[0].dayMs) : 0

  const cellTone = (day: DayRecord) => {
    const pct = day.changePercent
    if (pct === null || pct === 0) return 'bg-foreground/10'
    const strength = Math.abs(pct) / peak
    if (pct > 0)
      return strength > 0.66
        ? 'bg-chart-mint'
        : strength > 0.33
          ? 'bg-chart-mint/60'
          : 'bg-chart-mint/30'
    return strength > 0.66
      ? 'bg-chart-red'
      : strength > 0.33
        ? 'bg-chart-red/60'
        : 'bg-chart-red/30'
  }

  const signed = (value: number) =>
    `${value >= 0 ? '+' : '−'}${fmt.usd(Math.abs(value), { sensitive: true })}`

  return (
    <HomePanel title={t('title')} className={className}>
      {isPending ? (
        <Skeleton className="h-40 w-full" />
      ) : !summary || summary.days.length === 0 ? (
        <p className="text-foreground/50 py-6 text-sm">{t('empty')}</p>
      ) : (
        <div className="grid grid-cols-1 gap-5 md:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
          <div className="flex flex-col gap-1.5">
            <div className="text-foreground/35 grid grid-cols-7 gap-1 text-center text-[10px]">
              {WEEKDAYS.map((day) => (
                <span key={day}>{t(`weekdays.${day}`)}</span>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {Array.from({ length: leading }, (_, index) => (
                <span key={`pad-${index}`} />
              ))}
              {summary.days.map((day, index) => (
                <Tooltip key={day.dayMs}>
                  <TooltipTrigger asChild>
                    <motion.span
                      className={cn(
                        'aspect-square cursor-default rounded-sm transition-[filter] hover:brightness-125',
                        cellTone(day),
                        day.dayMs + DAY_MS > Date.now() && 'ring-shell-accent ring-1'
                      )}
                      initial={{ opacity: 0, scale: 0.6 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ ...HOME_SOFT_SPRING, delay: index * 0.012 }}
                    />
                  </TooltipTrigger>
                  <TooltipContent>
                    {shortDate(day.dayMs)} · {fmt.usd(day.close, { sensitive: true })}
                    {day.change !== null &&
                      ` · ${signed(day.change)} (${fmt.number(day.changePercent ?? 0, {
                        maximumFractionDigits: 2,
                      })}%)`}
                  </TooltipContent>
                </Tooltip>
              ))}
            </div>
          </div>
          <dl className="grid grid-cols-2 content-start gap-3 text-xs">
            <div className="flex flex-col gap-0.5">
              <dt className="text-foreground/45">{t('upDays')}</dt>
              <dd className="text-chart-mint text-lg">{summary.upDays}</dd>
            </div>
            <div className="flex flex-col gap-0.5">
              <dt className="text-foreground/45">{t('downDays')}</dt>
              <dd className="text-chart-red text-lg">{summary.downDays}</dd>
            </div>
            <div className="flex flex-col gap-0.5">
              <dt className="text-foreground/45">{t('bestDay')}</dt>
              <dd className="text-foreground">
                {summary.best ? (
                  <>
                    <span className="text-chart-mint">{signed(summary.best.change ?? 0)}</span>{' '}
                    <span className="text-foreground/40">{shortDate(summary.best.dayMs)}</span>
                  </>
                ) : (
                  '—'
                )}
              </dd>
            </div>
            <div className="flex flex-col gap-0.5">
              <dt className="text-foreground/45">{t('worstDay')}</dt>
              <dd className="text-foreground">
                {summary.worst ? (
                  <>
                    <span className="text-chart-red">{signed(summary.worst.change ?? 0)}</span>{' '}
                    <span className="text-foreground/40">{shortDate(summary.worst.dayMs)}</span>
                  </>
                ) : (
                  '—'
                )}
              </dd>
            </div>
            <div className="col-span-2 flex flex-col gap-1">
              <dt className="text-foreground/45 flex justify-between">
                {t('maxDrawdown')}
                <span className="text-ox-gold">
                  {fmt.number(summary.maxDrawdownPercent, { maximumFractionDigits: 1 })}%
                </span>
              </dt>
              <dd>
                <HomeMeter
                  value={Math.min(1, summary.maxDrawdownPercent / 50)}
                  barClassName="bg-ox-gold"
                />
              </dd>
            </div>
            <p className="text-foreground/35 col-span-2 text-[10px] leading-4">{t('note')}</p>
          </dl>
        </div>
      )}
    </HomePanel>
  )
}

/** The instruments the user trades most (30 days), with how much of each they fill as maker. */
export function HomeMostTraded({ className }: { className?: string }) {
  const t = useTranslations('trade.home.mostTraded')
  const fmt = useNumberFormat()
  const { stats, isPending } = useHomeTradeStats(30)
  const top = stats?.topInstruments ?? []
  const peak = Math.max(1, ...top.map((entry) => entry.volumeUsd))

  return (
    <HomePanel title={t('title')} className={className}>
      {isPending ? (
        <Skeleton className="h-40 w-full" />
      ) : top.length === 0 ? (
        <p className="text-foreground/50 py-6 text-sm">{t('empty')}</p>
      ) : (
        <motion.ul
          variants={HOME_LIST}
          initial="hidden"
          animate="show"
          className="flex flex-col gap-3"
        >
          {top.map((entry) => (
            <motion.li
              key={`${entry.exchange}:${entry.symbol}`}
              variants={HOME_ROW}
              className="flex flex-col gap-1"
            >
              <span className="flex items-center gap-2 text-xs">
                <ExchangeIcon
                  exchange={getExchangeIconKey(entry.exchange) ?? ''}
                  className="size-4"
                />
                <span className="text-foreground min-w-0 truncate">{entry.symbol}</span>
                <span className="text-foreground/35 hidden sm:inline">
                  {getExchangeLabel(entry.exchange)}
                </span>
                <span className="text-foreground ml-auto">
                  {fmt.usd(entry.volumeUsd, { sensitive: true })}
                </span>
              </span>
              <HomeMeter value={entry.volumeUsd / peak} barClassName="bg-foreground/60" />
              <span className="text-foreground/40 text-[10px]">
                {t('maker', { share: Math.round(entry.makerShare * 100) })}
              </span>
            </motion.li>
          ))}
        </motion.ul>
      )}
    </HomePanel>
  )
}
